import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type GetReprocessingInvoicesPayload,
  type ReprocessingInvoicesResponse,
} from './types';
import { zodReprocessingInvoicesQueryParamsSchema } from './validations';

/**
 * Fetches all invoices requiring reprocessing from the API.
 * @param searchParams - The search parameters for fetching the invoices.
 * @returns The reprocessing invoices data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_invoice_processes_invoice_process_get
 */
export const fetchReprocessingInvoices = async (
  searchParams: GetReprocessingInvoicesPayload,
): Promise<ReprocessingInvoicesResponse> => {
  try {
    const validatedSearchParams =
      zodReprocessingInvoicesQueryParamsSchema.parse(searchParams);

    // Filter out empty arrays to avoid sending them to the API
    const filteredParams = Object.fromEntries(
      Object.entries(validatedSearchParams).filter(
        ([_, value]) => !(Array.isArray(value) && value.length === 0),
      ),
    );

    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_REPROCESSING.GET_ALL(), {
        searchParams: filteredParams,
      })
      .json<ReprocessingInvoicesResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error fetching reprocessing invoices',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      errorMessage = i18n.t(($) => $.fetchReprocessingInvoices.error.generic, {
        ns: 'data',
      });
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
